import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { AccountModel } from 'src/app/models/account.model';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApiDataSource } from 'src/app/utils/api-data-source';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { tap } from 'rxjs/operators';
import { UsersSelectorComponent } from '../../users/users-selector/users-selector.component';
import { IndustriesModel } from '../../industries/industries.model';
import { AccountCopyTemplatesDialogComponent } from './../account-copy-templates-dialog/account-copy-templates-dialog.component';
import { AdminEmployeesSelectorComponent } from '../../../shared/admin-employees-selector/admin-employees-selector.component';
import { CURRENCIES, Currency } from '../../../constants/currencies.constants';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-accounts-edit',
  templateUrl: './accounts-edit.component.html',
  styleUrls: ['./accounts-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountsEditComponent implements OnInit {
  account: AccountModel = new AccountModel();

  industries: IndustriesModel[] = [];

  // Available currencies for the account currency selector.
  currencies: Currency[] = CURRENCIES;

  usersDisplayedColumns: string[] = [
    'user_id',
    'icons',
    'contact_person',
    'email',
    'mobile',
    'date_created',
    // Membership approval state (SC-301). These rows were always unfiltered by approval status, so a
    // pending request has always been listed here, but it rendered identically to a working membership.
    // Support could therefore look straight at the reason a user could not reach the account and see
    // nothing wrong.
    'approval_status',
    'rbac_roles',
    'actions'
  ];

  dataSource = new AccountsUsersDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  clientColumns: string[] = ['id', 'name', 'subscription'];
  @ViewChild('contractorOf') contractorOfTable: MatTable<any>;

  inProgress: boolean = false;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private route: ActivatedRoute,
    private rbac: RBACService
  ) {}

  ngOnInit() {
    this.account.id = Number(this.route.snapshot.params['id']);
    if (this.account.id) {
      this.getAccount();
    } else {
      // Pre-select the deployment default currency (region-based) for new accounts.
      this.account.currency_code = this.utils.getDefaultCurrencyCode();
    }

    // Get industries from the API.
    this.getIndustries();
  }

  ngAfterViewInit() {
    if (!this.account.id) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  getOutputTimezone($event) {
    this.account.timezone = $event;
  }

  private async getIndustries() {
    this.industries = await this.utils.getIndustries();
  }

  getAccount() {
    return this.api
      .makeRequest('get', `v2/admin/accounts/${this.account.id}`)
      .then((account: AccountModel) => {
        this.account.reset();
        this.account.apply(account);

        // Need to call this since the table listing contractors references an array and not an observable data source.
        this.contractorOfTable.renderRows();
        // Set the account id in the datasource and get the data
        this.dataSource.accountId = this.account.id;
        this.dataSource.getData();
      });
  }

  // Redirect the user to the specified account by id
  onRedirectToAccount(clientId: number) {
    this.utils.refreshPage(['/', 'admin', 'accounts', clientId, 'edit']);
  }

  // Redirect the user to the specified user by id
  onRedirectToUser(userId: number) {
    this.utils.refreshPage(['/', 'admin', 'users', userId, 'edit']);
  }

  /**
   * Send a request to the APi to create or update an account.
   * @param form
   */
  onSubmit(form: NgForm) {
    // Check if the form is valid.
    if (!form.valid) {
      this.utils.showModal(
        'Form Validation Failed',
        'Please enter all required fields.'
      );
      return;
    }
    // Set the in progress status to true.
    this.inProgress = true;
    // If the account id is present then we are updating an existing account.
    if (this.account.id) {
      // Update an existing account.
      this.api
        .makeRequest(
          'put',
          `v2/admin/accounts/${this.account.id}`,
          this.account
        )
        .then((response: AccountModel) => {
          this.account.apply(response);
          this.utils.showToast('The account was successfully updated.');
        })
        .finally(() => {
          this.inProgress = false;
        });
    } else {
      // Create a new account.
      this.api
        .makeRequest('post', `v2/admin/accounts`, this.account)
        .then((response: AccountModel) => {
          this.utils.showToast('The new account was successfully created.');
          this.utils.refreshPage(['/admin', 'accounts', response.id, 'edit']);
        })
        .finally(() => {
          this.inProgress = false;
        });
    }
  }

  /**
   * Save the system (danger zone) settings, e.g. the internal account flag. Only applies to an
   * existing account. Reuses the same admin update endpoint as onSubmit().
   */
  onSaveSystem() {
    if (!this.account.id) {
      return;
    }

    const doSave = (): void => {
      this.inProgress = true;
      this.api
        .makeRequest(
          'put',
          `v2/admin/accounts/${this.account.id}`,
          this.account
        )
        .then((response: AccountModel) => {
          this.account.apply(response);
          this.utils.showToast('The account was successfully updated.');
        })
        .finally(() => {
          this.inProgress = false;
        });
    };

    // Safety net: confirm before marking as an internal account. Disabling it saves without a prompt.
    if (this.account.is_internal) {
      this.utils.showModal(
        'Confirm Internal Account',
        'You are about to mark this as an internal ' +
          this.utils.getAppName() +
          ' account. Internal accounts can manage support (super admin) users across the platform. Are you sure?',
        doSave
      );
    } else {
      doSave();
    }
  }

  /**
   * Get the RBAC roles that can still be assigned for a row (the account's roles the user does not
   * already hold).
   *
   * @param row The users-accounts row (carries account_id, user_id, rbac_roles, available_rbac_roles).
   */
  getAssignableRoles(row: any): any[] {
    const assignedIds: number[] = (row.rbac_roles || []).map((r: any) => r.id);
    return (row.available_rbac_roles || []).filter(
      (r: any) => !assignedIds.includes(r.id)
    );
  }

  /**
   * Assign a single RBAC role to a user for this account (super admin support action).
   *
   * @param accountId The account the role belongs to.
   * @param userId The user to assign the role to.
   * @param role The RBAC role ({ id, name }).
   */
  onAddRbacRole(accountId: number, userId: number, role: any) {
    this.api
      .makeRequest(
        'post',
        `v2/admin/users-accounts/${accountId}/${userId}/rbac-role/${role.id}`
      )
      .then(() => {
        this.utils.showToast('The RBAC role was assigned.');
        this.dataSource.getData();
      })
      .catch((_error) => {
        this.utils.handleAPIErrors(_error);
      });
  }

  /**
   * Unlink a single RBAC role from a user for this account (super admin support action).
   *
   * @param accountId The account the role belongs to.
   * @param userId The user to remove the role from.
   * @param role The RBAC role ({ id, name }).
   */
  onUnlinkRbacRole(accountId: number, userId: number, role: any) {
    this.utils.showModal(
      'Unlink RBAC Role',
      'Are you sure you want to unlink the "' +
        role.name +
        '" role from this user in this account?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v2/admin/users-accounts/${accountId}/${userId}/rbac-role/${
              role.id
            }`
          )
          .then(() => {
            this.utils.showToast('The RBAC role was unlinked.');
            this.dataSource.getData();
          })
          .catch((_error) => {
            this.utils.handleAPIErrors(_error);
          });
      }
    );
  }

  onSelectUsers() {
    this.utils.showComponentDialog(
      UsersSelectorComponent,
      {
        multiple: true,
        selected: this.account.users.map((user) => user.id)
      },
      {
        disableClose: false,
        width: '1200px'
      },
      (userIds: any[]) => {
        if (userIds && userIds.length > 0) {
          this.utils.showModal(
            'Link Selected Users',
            'Are you sure you want to link the selected users with this account?',
            () => {
              this.inProgress = true;
              this.api
                .makeRequest('post', `v2/admin/users-accounts`, {
                  account_ids: [this.account.id],
                  user_ids: userIds
                })
                .then((response: any) => {
                  // Warn when the partner role could not be applied for a linked partner (api-spec 6.7).
                  this.utils.showToast(
                    'The users were linked to the account.' +
                      this.rbac.partnerSyncFailureNotice(response)
                  );
                  this.dataSource.getData(true);
                  this.inProgress = false;
                })
                .catch(() => {
                  this.inProgress = false;
                })
                .then(() => {
                  this.getAccount();
                });
            }
          );
        }
      }
    );
  }

  /**
   * Plain-language explanation of a membership state, for support reading this screen.
   *
   * Falls back to the pending wording when the value is missing rather than showing nothing, because an
   * absent status is the fail-closed database default and means the same thing in practice.
   */
  getMembershipTooltip(status: string): string {
    switch (status) {
      case 'approved':
        return 'This user is a member of the account and can see its data.';
      case 'pending':
        return 'This user has asked to join and is waiting for the account to approve them. They cannot see any of the account\'s data yet.';
      case 'declined':
        return 'The account refused this request. The user cannot see any of the account\'s data.';
      default:
        return 'This membership has not been approved, so the user cannot see the account\'s data.';
    }
  }

  /**
   * Approve a membership on the account's behalf (super admin support action).
   *
   * Confirmed first, and the confirmation spells out the consequences rather than asking a bare "are you
   * sure". This is somebody acting on a customer's behalf, and it is not a reversible tidy-up: the user is
   * emailed, their employer moves, and they gain sight of the account's data. Approving the wrong row hands
   * one customer's information to the wrong person, so the prompt names who and lists what follows.
   *
   * A modal rather than the quick-actions popover used by Delete. The popover is anchored to the button and
   * has no width constraint, so it grows sideways until it leaves the page: it suits a one-line "are you
   * sure", which is all Delete needs. This action cannot be reduced to one line without dropping the
   * consequences, and the consequences are the reason to confirm at all. onUnlinkRbacRole on this same
   * screen already uses a modal for the same reason.
   */
  onApproveMembership(user: any) {
    this.utils.showModal(
      'Approve Membership',
      `<p>Approve <b>${user.contact_person}</b> as a member of this account, on the account's behalf?</p>` +
        '<p>This will:</p>' +
        '<ul>' +
        '<li>Email them to say they were approved.</li>' +
        '<li>Give them their default role in this account.</li>' +
        '<li>Let them see this account\'s data.</li>' +
        '</ul>',
      () => {
        this.inProgress = true;
        this.api
          .makeRequest(
            'put',
            `v2/admin/users-accounts/${user.account_id}/${user.user_id}/approve`
          )
          .then(() => {
            this.utils.showToast('The membership was approved.');
            this.dataSource.getData();
            if (this.account.id) {
              this.getAccount();
            }
          })
          .catch((_error) => {
            this.utils.handleAPIErrors(_error);
          })
          .finally(() => {
            this.inProgress = false;
          });
      }
    );
  }

  /**
   * Send an API request to unlink the user from the account.
   * @param user
   * @param evt
   */
  onDestroyUser(user: any, evt: any) {
    // Get confirmation.
    this.utils.showQuickActions(
      evt.target,
      `Are you sure you want to remove the user '${
        user.contact_person
      }' from the account?`,
      [
        {
          text: 'Yes',
          handler: () => {
            this.inProgress = true;
            this.api
              .makeRequest(
                'delete',
                `v2/admin/users-accounts/${user.account_id}/${user.user_id}`
              )
              .then(() => {
                this.utils.showToast('The user was removed from the account.');
                this.dataSource.getData(true);
                if (this.account.id) {
                  this.getAccount();
                }
              })
              .catch((_error) => {
                // Show the API's reason, e.g. the last administrator cannot be removed.
                this.utils.handleAPIErrors(_error);
              })
              .finally(() => {
                this.inProgress = false;
              });
          }
        },
        {
          text: 'No',
          handler: () => {}
        }
      ]
    );
  }

  /**
   * Open the dialog to copy templates from.
   */
  onOpenCopyTemplates() {
    this.utils.showComponentDialog(
      AccountCopyTemplatesDialogComponent,
      {
        targetAccountId: this.account.id
      },
      { width: '768px' },
      (results) => {}
    );
  }

  /**
   * Make an API request to re-sync account inductions.
   */
  onResyncAccountInductons() {
    // Confirm before running the induction sync process.
    this.utils.showModal(
      'Re-sync Account Inductions',
      'Are you sure you want to re-sync account inductions?',
      () => {
        this.inProgress = true;
        // Make the API request to re-sync account inductions.
        this.api
          .makeRequest(
            'get',
            `v2/admin/account/re-sync-inductions/${this.account.id}`
          )
          .then((response) => {
            this.utils.showToast('Account inductions were re-synced.');
          })
          .catch((err) => {
            this.utils.showToast('Failed to run account inductions sync.');
          })
          .finally(() => {
            this.inProgress = false;
          });
      }
    );
  }

  onSelectMainContact() {
    this.utils.showComponentDialog(
      AdminEmployeesSelectorComponent,
      {
        multiple: false,
        deselect: false,
        selected: [this.account.main_contact_id],
        accountId: this.account.id
      },
      {
        width: '1200px'
      },
      (userId: number) => {
        if (typeof userId == 'undefined') {
          return;
        }

        this.account.main_contact_id = userId;
      }
    );
  }
}

export class AccountsUsersDataSource extends ApiDataSource {
  order_by = 'contact_person';
  order = 'asc';

  accountId: number;

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/admin/users-accounts`, resetOffset, {
      admin_account_id: this.accountId
    });
  }
}
